<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSubjectsStore } from '@/stores/subjects'

const route = useRoute()
const router = useRouter()
const incidentId = route.params.id as string

const subjectsStore = useSubjectsStore()
const subjects = computed(() => subjectsStore.subjects)

function go() {
  router.push({ name: 'incident-subjects', params: { id: incidentId } })
}
</script>

<template>
  <div class="bg-surface border border-border rounded-lg p-4">
    <div class="flex items-center justify-between mb-3">
      <span class="text-[10px] font-label uppercase tracking-widest text-outline">
        {{ subjects.length === 1 ? 'Subject' : `Subjects (${subjects.length})` }}
      </span>
      <button
        class="p-0 text-[11px] text-primary font-label uppercase tracking-wide hover:underline"
        @click="go"
      >
        View subjects →
      </button>
    </div>

    <div v-if="subjects.length" class="flex flex-col divide-y divide-border">
      <div
        v-for="(s, index) in subjects"
        :key="s.subject_id"
        :class="['flex gap-3', index > 0 ? 'pt-3' : '', index < subjects.length - 1 ? 'pb-3' : '']"
      >
        <!-- Photo -->
        <div class="shrink-0">
          <img
            v-if="s.photo_url"
            :src="s.photo_url"
            :alt="`${s.first_name} ${s.last_name}`"
            class="w-12 h-12 rounded object-cover border border-border"
          />
          <div
            v-else
            class="w-12 h-12 rounded border border-border bg-surface-container-low flex items-center justify-center"
          >
            <img src="/assets/subject-placeholder.svg" alt="Subject" class="w-7 h-7 opacity-40" />
          </div>
        </div>

        <!-- Details -->
        <div class="flex-1 min-w-0">
          <!-- Name row + risk badge -->
          <div class="flex items-baseline gap-2 flex-wrap">
            <span class="text-sm font-semibold text-on-surface leading-tight">
              {{ s.first_name }} {{ s.last_name }}
            </span>
            <span class="text-[11px] text-outline">
              {{ s.subject_category }}<template v-if="s.gender || s.age"> · {{ s.gender }}{{ s.age ? `/${s.age}` : '' }}</template>
            </span>
            <span
              v-if="s.risk_category"
              :class="[
                'inline-block px-1.5 py-px rounded text-[10px] font-label uppercase tracking-wide border',
                s.risk_category === 'High' ? 'bg-error-container text-error border-error/30' :
                s.risk_category === 'Medium' ? 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700' :
                'bg-surface-container text-outline border-border'
              ]"
            >
              {{ s.risk_category }} Risk
            </span>
          </div>

          <!-- Inline field pills -->
          <div class="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
            <span v-if="s.lkp_description" class="text-[11px] text-on-surface-variant">
              <span class="text-[10px] font-label uppercase tracking-wide text-outline mr-1">LKP</span>{{ s.lkp_description }}
            </span>
            <span v-if="s.intended_dest" class="text-[11px] text-on-surface-variant">
              <span class="text-[10px] font-label uppercase tracking-wide text-outline mr-1">Dest</span>{{ s.intended_dest }}
            </span>
            <span v-if="s.mobility" class="text-[11px] text-on-surface-variant">
              <span class="text-[10px] font-label uppercase tracking-wide text-outline mr-1">Mobility</span>{{ s.mobility }}
            </span>
            <span v-if="s.medical_conditions" class="text-[11px] text-on-surface-variant">
              <span class="text-[10px] font-label uppercase tracking-wide text-outline mr-1">Medical</span>{{ s.medical_conditions }}
            </span>
            <span v-if="s.clothing_description" class="text-[11px] text-on-surface-variant truncate max-w-xs">
              <span class="text-[10px] font-label uppercase tracking-wide text-outline mr-1">Clothing</span>{{ s.clothing_description }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <p v-else class="text-sm text-outline italic">No subject added yet.</p>
  </div>
</template>
